// Tooltip component for displaying helpful information
import { useState, useRef, useEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@utils/classNames';

export interface TooltipProps {
  content: string | ReactNode;
  children: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  className?: string;
  /** Overrides default `inline-block` on the hover/focus trigger wrapper (e.g. `block w-full min-w-0` for list rows). */
  triggerClassName?: string;
  disabled?: boolean;
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = 'top',
  delay = 300,
  className,
  triggerClassName,
  disabled = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isTouchViewport, setIsTouchViewport] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const calculatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;
    const gap = 8;

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = triggerRect.top + scrollY - tooltipRect.height - gap;
        left = triggerRect.left + scrollX + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + scrollY + gap;
        left = triggerRect.left + scrollX + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
      case 'left':
        top = triggerRect.top + scrollY + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.left + scrollX - tooltipRect.width - gap;
        break;
      case 'right':
        top = triggerRect.top + scrollY + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.right + scrollX + gap;
        break;
    }

    // Keep tooltip within viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (left < 10) left = 10;
    if (left + tooltipRect.width > viewportWidth - 10) {
      left = viewportWidth - tooltipRect.width - 10;
    }
    if (top < 10) top = 10;
    if (top + tooltipRect.height > viewportHeight + scrollY - 10) {
      top = viewportHeight + scrollY - tooltipRect.height - 10;
    }

    setTooltipPosition({ top, left });
  };

  const showTooltip = () => {
    if (disabled || isTouchViewport) return;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
      // Calculate position after a brief delay to ensure tooltip is rendered
      setTimeout(calculatePosition, 10);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(false);
  };

  useEffect(() => {
    const updateTouchViewport = () => {
      const coarsePointer = typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(pointer: coarse)').matches;
      const smallViewport = typeof window !== 'undefined' && window.innerWidth < 768;
      setIsTouchViewport(coarsePointer || smallViewport);
    };

    updateTouchViewport();
    window.addEventListener('resize', updateTouchViewport);
    return () => window.removeEventListener('resize', updateTouchViewport);
  }, []);

  useEffect(() => {
    if (isVisible) {
      calculatePosition();
      const handleResize = () => calculatePosition();
      const handleScroll = () => calculatePosition();
      
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isVisible, position]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        className={triggerClassName ?? 'inline-block'}
      >
        {children}
      </div>
      
      {isVisible && !disabled && !isTouchViewport && typeof window !== 'undefined' && createPortal(
        <div
          ref={tooltipRef}
          className={cn(
            'fixed z-[9300] pointer-events-none',
            'animate-fade-in',
            className
          )}
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
          }}
        >
          <div className="bg-dark-secondary/95 backdrop-blur-md border border-white/20 rounded-lg px-3 py-2 shadow-xl">
            <div className="font-inter text-xs text-white/90 whitespace-nowrap">
              {content}
            </div>
            {/* Arrow */}
            <div
              className={cn(
                'absolute w-2 h-2 bg-dark-secondary/95 border border-white/20 rotate-45',
                {
                  'bottom-[-4px] left-1/2 -translate-x-1/2 border-t-0 border-r-0':
                    position === 'top',
                  'top-[-4px] left-1/2 -translate-x-1/2 border-b-0 border-l-0':
                    position === 'bottom',
                  'right-[-4px] top-1/2 -translate-y-1/2 border-l-0 border-b-0':
                    position === 'left',
                  'left-[-4px] top-1/2 -translate-y-1/2 border-r-0 border-t-0':
                    position === 'right',
                }
              )}
            />
          </div>
        </div>,
        document.body
      )}
    </>
  );
};































































